import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Smartphone, 
  Settings, 
  TrendingUp, 
  CheckCircle2,
  AlertCircle,
  Save
} from 'lucide-react';
import api from '../../lib/api';

const Security2FATab = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    threshold_amount: 1000000,
    statistics: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/security/2fa/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Erreur chargement paramètres 2FA:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement des paramètres'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setMessage(null);

      await api.put('/security/2fa/settings', {
        enabled: settings.enabled,
        threshold_amount: parseFloat(settings.threshold_amount)
      });

      setMessage({
        type: 'success',
        text: 'Paramètres 2FA enregistrés avec succès'
      });

      // Recharger les stats
      await fetchSettings();
    } catch (error) {
      console.error('Erreur sauvegarde 2FA:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Erreur lors de la sauvegarde'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const stats = settings.statistics || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Authentification à 2 Facteurs (2FA)
          </h2>
          <p className="text-gray-600 mt-1">
            Sécurisez les demandes d'avance importantes avec une vérification SMS
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div 
          className={`p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
            {message.text}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Configuration
          </h3>

          <div className="space-y-6">
            {/* Activer/Désactiver 2FA */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Activer la 2FA
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Demander un code SMS pour les montants élevés
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Seuil de montant */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seuil de déclenchement (GNF)
              </label>
              <input
                type="number"
                value={settings.threshold_amount}
                onChange={(e) => setSettings({...settings, threshold_amount: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1000000"
                min="0"
                step="100000"
              />
              <p className="text-xs text-gray-500 mt-1">
                Les demandes d'avance ≥ ce montant nécessiteront une vérification 2FA
              </p>
              <p className="text-sm text-blue-600 mt-2 font-medium">
                {new Intl.NumberFormat('fr-FR').format(settings.threshold_amount)} GNF
              </p>
            </div>

            {/* Bouton Sauvegarder */}
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Enregistrer les paramètres
                </>
              )}
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gray-600" />
            Statistiques du jour
          </h3>

          <div className="space-y-4">
            {/* Statut */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Statut</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${settings.enabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <p className="text-sm font-semibold text-gray-800">
                  {settings.enabled ? 'Activée' : 'Désactivée'}
                </p>
              </div>
            </div>

            {/* Codes envoyés */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Codes envoyés</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.codes_sent_today || 0}
              </p>
            </div>

            {/* Codes vérifiés */}
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Codes vérifiés</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.codes_verified_today || 0}
              </p>
            </div>

            {/* Taux de succès */}
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Taux de succès</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.success_rate ? `${stats.success_rate.toFixed(1)}%` : '0%'}
              </p>
            </div>

            {/* Seuil configuré */}
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Seuil actuel</p>
              <p className="text-sm font-bold text-orange-600">
                {new Intl.NumberFormat('fr-FR').format(stats.threshold_amount || 0)} GNF
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Comment fonctionne la 2FA ?</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Lorsqu'un employé demande une avance ≥ au seuil configuré, un code SMS est automatiquement envoyé</li>
          <li>L'employé doit entrer ce code pour valider sa demande</li>
          <li>Le code est valable 10 minutes</li>
          <li>Cette sécurité additionnelle protège contre les demandes frauduleuses</li>
        </ul>
      </div>
    </div>
  );
};

export default Security2FATab;
